import { runInAction } from "mobx";
import { initComponentStore, noop } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { THEMES, LANGUAGES, Theme, Language } from "@/app_store/settings";
import type { Account } from "@/app_store/account";
import type { Project } from "@/app_store/project";


const MOBILE_QUERY = "(max-width: 768px)";

export class ActivityBarStore {
    section: "code" | "review" | "deploy" = "code";
    onSectionChange: (s: "code" | "review" | "deploy") => any = noop;
    isAgentOpen: boolean = false;
    onAgentToggle: () => any = noop;
    isAccountMenuOpen: boolean = false;
    isSettingsModalOpen: boolean = false;
    settingsTab: "appearance" | "claude" | "tabs" | "ssh-keys" = "appearance";
    isSetupAgentModalOpen: boolean = false;
    isZoomOpen: boolean = false;
    uiZoom: number = 100;
    isMobile: boolean = window.matchMedia(MOBILE_QUERY).matches;

    constructor() {
        initComponentStore(this);

        const mq = window.matchMedia(MOBILE_QUERY);
        mq.addEventListener("change", (e) => {
            runInAction(() => { this.isMobile = e.matches; });
        });
    }

    get account(): Account | null {
        return appStore.account.account;
    }

    get projects(): Project[] {
        return appStore.project.all;
    }

    get selectedProjectId() {
        return appStore.project.selectedProjectId;
    }

    setSection(section: "code" | "review" | "deploy") {
        this.section = section;
        this.onSectionChange(section);
    }

    toggleAgent() {
        this.onAgentToggle();
    }

    toggleAccountMenu() {
        this.isAccountMenuOpen = !this.isAccountMenuOpen;
    }

    closeAccountMenu() {
        this.isAccountMenuOpen = false;
    }

    openZoom() {
        this.isAccountMenuOpen = false;
        this.isZoomOpen = true;
    }

    closeZoom() {
        this.isZoomOpen = false;
    }

    setUiZoom(level: number) {
        this.uiZoom = Math.max(50, Math.min(200, level));
        const z = this.uiZoom / 100;
        document.documentElement.style.zoom = z.toString();
        document.documentElement.style.setProperty('--ui-zoom-inv', (1 / z).toString());
        const vv = window.visualViewport;
        if (vv) {
            document.documentElement.style.setProperty('--app-height', `${vv.height / z}px`);
        }
    }

    selectProject(id: string) {
        appStore.project.selectProject(id);
        this.isAccountMenuOpen = false;
    }

    closeProject() {
        appStore.project.closeProject();
        this.isAccountMenuOpen = false;
    }

    get themes(): Theme[] {
        return THEMES;
    }

    get currentTheme(): string {
        if (this.isMobile) {
            return appStore.settings.currentSettings?.theme_mobile ?? "darcula";
        }
        return appStore.settings.currentSettings?.theme_desktop ?? "darcula";
    }

    get languages(): Language[] {
        return LANGUAGES;
    }

    get currentLanguage(): string {
        return appStore.settings.currentSettings?.language ?? "en";
    }

    get currentMaxOpenTabsDesktop(): number {
        return appStore.settings.currentSettings?.max_open_tabs_desktop ?? 10;
    }

    get currentMaxOpenTabsMobile(): number {
        return appStore.settings.currentSettings?.max_open_tabs_mobile ?? 2;
    }

    get currentClaudeModel(): string {
        return appStore.settings.currentSettings?.claude_model ?? "";
    }

    setClaudeModel(model: string) {
        appStore.settings.testSettings({ claude_model: model });
    }

    get currentClaudeEffort(): string {
        return appStore.settings.currentSettings?.claude_effort ?? "";
    }

    setClaudeEffort(effort: string) {
        appStore.settings.testSettings({ claude_effort: effort });
    }

    previewMaxOpenTabsDesktop(n: number) {
        appStore.settings.testSettings({ max_open_tabs_desktop: n });
    }

    previewMaxOpenTabsMobile(n: number) {
        appStore.settings.testSettings({ max_open_tabs_mobile: n });
    }

    get token(): string | null {
        return appStore.account.token;
    }

    get apiUrl(): string {
        return appStore.apiUrl;
    }

    openSetupAgentModal() {
        this.isAccountMenuOpen = false;
        this.isSetupAgentModalOpen = true;
    }

    closeSetupAgentModal() {
        this.isSetupAgentModalOpen = false;
    }

    openSettingsModal() {
        this.isAccountMenuOpen = false;
        this.isSettingsModalOpen = true;
        this.settingsTab = "appearance";
    }

    setSettingsTab(tab: "appearance" | "claude" | "tabs" | "ssh-keys") {
        this.settingsTab = tab;
    }

    previewTheme(themeId: string) {
        if (this.isMobile) {
            appStore.settings.testSettings({ theme_mobile: themeId });
        } else {
            appStore.settings.testSettings({ theme_desktop: themeId });
        }
    }

    previewLanguage(languageId: string) {
        appStore.settings.testSettings({ language: languageId });
    }

    saveSettings() {
        appStore.settings.saveSettings();
        this.isSettingsModalOpen = false;
    }

    cancelSettings() {
        appStore.settings.revertSettings();
        this.isSettingsModalOpen = false;
    }

    refreshApp() {
        this.isAccountMenuOpen = false;
        appStore.startRefresh();
    }

    signOut() {
        this.isAccountMenuOpen = false;
        appStore.account.signOut();
    }
}
